/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnDestroy, type OnInit, type SimpleChanges, Component, ElementRef, Input } from '@angular/core';
import { type Polling, type Rest, EventService, ExtendService, HttpService, LazyloadService, PermissionService, PollingService } from '@dm/core';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { LicenseKey } from 'app/common/constants/license-key';
import { MO } from 'app/common/constants/mo';
import { VstoreIdE } from 'app/common/constants/vstore';
import { type ProtectionFileSystem, type ProtectionLun } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { SnapSourceSelectTypeE } from 'app/feature/protection/object/lun/snapshot/create-form/create-lun-snapshot-form.enum';
import { ScheduleService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule.service';
import { SimpleCreateSnapComponent } from 'app/feature/protection/simple-protection/simple-create-snap/simple-create-snap.component';
import { CreateSnapShotFromPageE } from 'app/feature/protection/simple-protection/simple-create-snap/simple-create-snap.enum';
import { delay } from 'lodash';
import { type Observable, type Subscription, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { OBJECT_QUERY_RANGE } from '../smart-assistant.const';
import { SmartAssistantProtectionStrService } from './object-protection-str.service';

@Component({
    selector: 'app-smart-assistant-object-protection',
    templateUrl: './object-protection.component.html',
    styleUrls: ['./object-protection.component.scss'],
})
export class SmartAssistantProtectionComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * 是否展示动图区域
     */
    @Input() showImage = true;
    /**
     * 最大高度
     */
    @Input() maxHeight = '100%';
    /**
     * 国际化
     */
    i18n = this.str.i18n;
    /**
     * 对象保护标题
     */
    subTitle: string;
    /**
     * LUN
     */
    lunDatas: ProtectionLun[] = [];
    /**
     * 文件系统
     */
    fsDatas: ProtectionFileSystem[] = [];
    /**
     * 是否处于加载中
     */
    loading = true;

    /**
     * LUN轮询
     */
    lunPolling$: Polling;
    /**
     * LUN轮询的订阅
     */
    lunSub$: Subscription;
    /**
     * 文件系统轮询
     */
    fsPolling$: Polling;
    /**
     * 文件系统轮询的订阅
     */
    fsSub$: Subscription;

    /**
     * 有LUN的“写”权限
     */
    hasLunWrite: boolean;
    /**
     * 有文件系统的“写”权限
     */
    hasFsWrite: boolean;
    /**
     * 有LUN或文件系统“写”权限
     */
    hasWritePermission: boolean;
    /**
     * 有LUN的License
     */
    hasLunLicense: boolean;
    /**
     * 是否是折叠状态（首页使用）
     */
    isCollapse = false;
    /**
     * 常量
     */
    CreateSnapShotFromPageE = CreateSnapShotFromPageE;

    constructor(
        public str: SmartAssistantProtectionStrService,
        public el: ElementRef,
        private permissionService: PermissionService,
        private ext: ExtendService,
        private scheduleService: ScheduleService,
        private pollingService: PollingService,
        private http: HttpService,
        private lazyloadService: LazyloadService,
        private eventService: EventService,
    ) { }

    ngOnInit(): void {
        // 没有“写”权限，就不展示了
        this.hasLunWrite = this.permissionService.checkShow(['lun_W', 'lun_snapshot_W']);
        this.hasFsWrite = this.permissionService.checkShow(['file_system_W', 'fs_snapshot_W']);
        this.hasWritePermission = this.hasLunWrite || this.hasFsWrite;
        this.hasLunLicense = this.permissionService.isLicenseExist([LicenseKey.SmartThin]);
        if (!this.hasWritePermission) {
            this.loading = false;
            return;
        }

        this.ext.forkAll([
            this.handleData(this.getLunRest(), MO.LUN),
            this.handleData(this.getFsRest(), MO.FILE_SYSTEM),
        ]).pipe(
            finalize(() => {
                this.loading = false;
                delay(this.setPolling, 15000);
            }),
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { showImage } = changes;
        if (showImage && showImage.currentValue !== showImage.previousValue) {
            this.isCollapse = !showImage.currentValue;
        }
    }

    getLunRest(): Rest {
        return this.scheduleService.getLun(VstoreIdE.sys).setParams({
            sortby: 'createTime,d',
            range: OBJECT_QUERY_RANGE,
        });
    }

    getFsRest(): Rest {
        return this.scheduleService.getBatchFsRest(VstoreIdE.sys).setParams({
            sortby: 'createTime,d',
            range: OBJECT_QUERY_RANGE,
        });
    }

    /**
     * 设置LUN/FS的轮询
     */
    setPolling = (): void => {
        if (this.hasLunWrite && this.hasLunLicense) {
            this.lunPolling$ = this.pollingService.add(this.getLunRest(), 15);
            this.lunSub$ = this.lunPolling$.subscribe(({ data }: { data: ProtectionLun[] }) => {
                this.lunDatas = data || [];
            });
        }

        if (this.hasFsWrite && SWITCH.SUPPORT_NAS) {
            this.fsPolling$ = this.pollingService.add(this.getFsRest(), 15);
            this.fsSub$ = this.fsPolling$.subscribe(({ data }: { data: ProtectionFileSystem[] }) => {
                this.fsDatas = data || [];
            });
        }
    };

    /**
     * 处理返回结果
     * @param data LUN/文件系统的rest
     * @param objType LUN/文件系统
     */
    handleData(rest: Rest, objType: MO): Observable<{ data: ProtectionLun[] } | { data: ProtectionFileSystem[] }> {
        if (objType === MO.LUN) {
            return this.hasLunWrite && this.hasLunLicense ?
                this.http.get(rest).pipe(
                    tap(({ data }: { data: ProtectionLun[] }) => {
                        this.lunDatas = data || [];
                    })) :
                of(null);
        }

        return this.hasFsWrite && SWITCH.SUPPORT_NAS ?
            this.http.get(rest).pipe(
                tap(({ data }: { data: ProtectionFileSystem[] }) => {
                    this.fsDatas = data || [];
                })) :
            of(null);
    }

    /**
     * 给LUN/文件系统创建保护
     */
    createProtection(fromPage: CreateSnapShotFromPageE, data: ProtectionLun | ProtectionFileSystem): void {
        this.eventService.emit(EventServiceKeyE.ClickAssistantCreateBtn);
        this.lazyloadService.appendComponent(SimpleCreateSnapComponent, {
            fromPage,
            type: SnapSourceSelectTypeE.Multi,
            datas: fromPage === CreateSnapShotFromPageE.ProtectLun ? [data] : [],
            selectedFs: fromPage === CreateSnapShotFromPageE.Fs ? [data] : [],
            showObjType: true,
            showSchedule: true,
        });
    }

    ngOnDestroy(): void {
        if (this.lunPolling$) {
            this.pollingService.remove(this.lunPolling$);
            this.lunSub$?.unsubscribe();
        }

        if (this.fsPolling$) {
            this.pollingService.remove(this.fsPolling$);
            this.fsSub$?.unsubscribe();
        }
    }
}
